import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, LogOut, ExternalLink, ShieldAlert, FolderTree, Settings, BarChart3, Activity, CreditCard, Globe, Megaphone, Mail, Archive, Bell, Users, Store, KeyRound, Heart, Menu, X, LifeBuoy, Shield, CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AdminLogin from "@/pages/admin/AdminLogin";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";

const links = [
  { to: "/admin", end: true, label: "Tableau de bord", icon: LayoutDashboard },
  { to: "/admin/produits", label: "Produits", icon: Package },
  { to: "/admin/commandes", label: "Commandes", icon: ShoppingCart },
  { to: "/admin/categories", label: "Catégories", icon: FolderTree },
  { to: "/admin/magasins", label: "Magasins", icon: Store },
  { to: "/admin/restock", label: "Alertes Stock", icon: Bell },
  { to: "/admin/reglages", label: "Réglages", icon: Settings },
];

const superLinks = [
  { to: "/admin/utilisateurs", label: "Utilisateurs", icon: Users },
  { to: "/admin/statistiques", label: "Statistiques", icon: BarChart3 },
  {
    to: "/admin/regie", label: "Régie Publicitaire", icon: Megaphone,
    children: [
      { to: "/admin/regie", end: true, label: "Vue d'ensemble" },
      { to: "/admin/regie/visiteurs-organiques", label: "Visiteurs organiques" },
    ],
  },
  { to: "/admin/activite", label: "Activité", icon: Activity },
  { to: "/admin/stripe", label: "Paiement Stripe", icon: CreditCard },
  { to: "/admin/communication", label: "Communication", icon: Mail },
  {
    to: "/admin/fidelite", label: "Fidélité", icon: Heart,
    children: [
      { to: "/admin/fidelite", end: true, label: "Campagnes" },
      { to: "/admin/fidelite/coupons", label: "Coupons" },
    ],
  },
  { to: "/admin/seo", label: "SEO", icon: Globe },
  { to: "/admin/sauvegarde", label: "Sauvegarde", icon: Archive },
  { to: "/admin/support", label: "Support", icon: LifeBuoy },
  { to: "/admin/abonnement", label: "Abonnement", icon: CalendarClock },
];

const rootLinks = [
  { to: "/admin/modules", label: "Modules & Accès", icon: KeyRound },
  {
    to: "/admin/securite", label: "Sécurité", icon: Shield,
    children: [
      { to: "/admin/securite", end: true, label: "Vue d'ensemble" },
      { to: "/admin/securite/blocage-automatique", label: "Blocage automatique" },
    ],
  },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [visibility, setVisibility] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "superadmin")) {
      api.get("/module-visibility").then(({ data }) => setVisibility(data.modules)).catch(() => setVisibility({}));
    }
  }, [user]);

  // Tous les hooks doivent être appelés avant les retours anticipés ci-dessous,
  // sinon React plante (erreur #310) en passant de l'écran de connexion à l'admin.
  const [alertCount, setAlertCount] = useState(0);
  useEffect(() => {
    if (!user?.is_root) return undefined;
    let active = true;
    const tick = () => api.get("/root/security/alerts/count").then(({ data }) => active && setAlertCount(data.unread || 0)).catch(() => {});
    tick();
    const id = setInterval(tick, 60000);
    return () => { active = false; clearInterval(id); };
  }, [user]);

  if (loading) return <div className="min-h-screen bg-ink" />;
  if (!user) return <AdminLogin />;

  if (user.role !== "admin" && user.role !== "superadmin") {
    return (
      <div className="min-h-screen bg-ink text-cream flex flex-col items-center justify-center gap-4 px-6 text-center">
        <ShieldAlert className="h-12 w-12 text-gold" strokeWidth={1.2} />
        <h1 className="font-serif text-3xl">Accès réservé</h1>
        <p className="text-cream/60">Ce compte n&apos;a pas les droits administrateur.</p>
        <Link to="/" className="mt-4 bg-cream text-ink px-6 py-3 text-xs uppercase tracking-[0.15em]">Retour à la boutique</Link>
      </div>
    );
  }

  // Root is exempt from module-visibility restrictions — otherwise it could
  // lock itself out of the very screen used to manage those restrictions.
  const moduleKey = (to) => to.replace("/admin/", "").replace("/admin", "dashboard");
  const isVisible = (l) => {
    if (user.is_root || !visibility) return true;
    const cfg = visibility[moduleKey(l.to)];
    if (!cfg) return true;
    return cfg[user.role] !== false;
  };

  const allLinks = [
    ...links.filter(isVisible),
    ...(user.role === "superadmin" ? superLinks.filter(isVisible) : []),
    ...(user.is_root ? rootLinks : []),
  ];

  const renderLink = (l, { mobile } = {}) => {
    const isParentActive = l.children
      ? location.pathname === l.to || location.pathname.startsWith(l.to + "/")
      : false;
    // Un parent avec sous-menu reste ouvert après clic (pour révéler ses
    // enfants) ; seuls les liens "feuilles" ferment le tiroir mobile.
    const closeIfLeaf = mobile && !l.children ? () => setMobileOpen(false) : undefined;
    return (
      <div key={l.to} className={mobile ? undefined : "md:contents"}>
        <NavLink
          to={l.to}
          end={l.end}
          onClick={closeIfLeaf}
          data-testid={`${mobile ? "admin-mobile-nav-" : "admin-nav-"}${l.label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) =>
            mobile
              ? `flex items-center gap-3 px-3 py-3 rounded-sm text-sm transition-colors ${
                  (isActive || isParentActive) ? "bg-white/10 text-cream" : "text-cream/60 hover:text-cream"
                }`
              : `flex items-center gap-3 px-5 py-3 text-sm whitespace-nowrap transition-colors ${
                  (isActive || isParentActive) ? "bg-white/10 text-cream border-l-2 border-gold" : "text-cream/60 hover:text-cream"
                }`
          }
        >
          <l.icon className="h-4 w-4" strokeWidth={1.6} />
          {l.label}
          {l.to === "/admin/securite" && alertCount > 0 && (
            <span data-testid="alerts-badge" className="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-red-600 text-white" title={`${alertCount} alerte(s) de sécurité non lue(s)`}>{alertCount}</span>
          )}
        </NavLink>
        {l.children && isParentActive && (
          <div className={`flex flex-col pl-3 border-l border-white/10 ml-6 mb-1 ${mobile ? "" : "mb-2"}`}>
            {l.children.map((child) => (
              <NavLink
                key={child.to}
                to={child.to}
                end={child.end}
                onClick={mobile ? () => setMobileOpen(false) : undefined}
                data-testid={`${mobile ? "admin-mobile-subnav-" : "admin-subnav-"}${child.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.12em] transition-colors ${
                    isActive ? "text-gold" : "text-cream/50 hover:text-cream"
                  }`
                }
              >
                <span className="w-1 h-1 rounded-full bg-current opacity-60" />
                {child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-sand flex flex-col md:flex-row">
      {/* Barre mobile : logo + bouton menu, seul point d'accès à la nav sous md */}
      <header className="md:hidden sticky top-0 z-30 bg-ink text-cream flex items-center justify-between px-4 py-3 border-b border-white/10">
        <Link to="/" aria-label="Déstockage Luminaires — accueil" className="inline-flex"><Logo to={null} size="sm" variant="light" testId="admin-layout-logo-mobile" /></Link>
        <button
          data-testid="admin-mobile-menu-toggle"
          onClick={() => setMobileOpen(true)}
          aria-label="Ouvrir le menu"
          className="p-2 -mr-2 text-cream/80 hover:text-cream"
        >
          <Menu className="h-6 w-6" />
        </button>
      </header>

      {/* Tiroir de navigation mobile : reprend tous les liens (y compris les
          sous-menus, invisibles en horizontal) + boutique/déconnexion, qui
          n'existaient sur aucun accès mobile auparavant. */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-ink/60" onClick={() => setMobileOpen(false)} aria-hidden="true" />
          <div data-testid="admin-mobile-drawer" className="relative w-[85%] max-w-xs bg-ink text-cream h-full overflow-y-auto flex flex-col">
            <div className="p-6 flex items-start justify-between border-b border-white/10">
              <div>
                <Logo to={null} size="sm" variant="light" testId="admin-layout-logo-drawer" />
                <p className="text-[10px] uppercase tracking-[0.25em] text-cream/40 mt-1">
                  {user.role === "superadmin" ? "Super Administration" : "Administration"}
                </p>
              </div>
              <button
                data-testid="admin-mobile-menu-close"
                onClick={() => setMobileOpen(false)}
                aria-label="Fermer le menu"
                className="p-2 -mr-2 -mt-2 text-cream/60 hover:text-cream"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 p-4 space-y-0.5">
              {allLinks.map((l) => renderLink(l, { mobile: true }))}
            </nav>
            <div className="p-4 border-t border-white/10 space-y-1">
              <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 text-xs text-cream/60 hover:text-cream">
                <ExternalLink className="h-4 w-4" /> Voir la boutique
              </a>
              <button
                data-testid="admin-mobile-logout"
                onClick={() => { logout(); navigate("/"); }}
                className="flex items-center gap-2 px-3 py-2 text-xs text-cream/60 hover:text-cream w-full"
              >
                <LogOut className="h-4 w-4" /> Déconnexion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar desktop */}
      <aside className="hidden md:flex md:w-64 bg-ink text-cream md:min-h-screen md:sticky md:top-0 md:h-screen md:flex-col">
        <div className="p-6">
          <Link to="/" aria-label="Déstockage Luminaires — accueil" className="inline-flex"><Logo to={null} size="sm" variant="light" testId="admin-layout-logo" /></Link>
          <p className="text-[10px] uppercase tracking-[0.25em] text-cream/40 mt-1">
            {user.role === "superadmin" ? "Super Administration" : "Administration"}
          </p>
        </div>
        <nav className="flex-1 p-4 overflow-y-auto">
          {allLinks.map((l) => renderLink(l))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 text-xs text-cream/60 hover:text-cream">
            <ExternalLink className="h-4 w-4" /> Voir la boutique
          </a>
          <button
            data-testid="admin-logout"
            onClick={() => { logout(); navigate("/"); }}
            className="flex items-center gap-2 px-3 py-2 text-xs text-cream/60 hover:text-cream w-full"
          >
            <LogOut className="h-4 w-4" /> Déconnexion
          </button>
        </div>
      </aside>

      <main className="flex-1 p-5 md:p-10 max-w-6xl">
        <Outlet />
      </main>
    </div>
  );
}
